import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, UserCheck, Bell, Mail, ShoppingBag, Truck, Headphones, BarChart, CheckCircle, Phone } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-600 to-purple-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-6">
              <Shield className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-xl text-pink-100">
              How we collect, use, and protect your personal information
            </p>
            <p className="mt-4 text-pink-200">Last Updated: December 1, 2023</p>
          </div>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Introduction */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6">Introduction</h2>
              <p className="text-gray-600 mb-6">
                At Sohkin Flowers ("we," "our," or "us"), we are committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you visit our website or make a purchase from us.
              </p>
              <div className="bg-pink-50 p-6 rounded-xl">
                <p className="font-bold mb-2">By using our services, you agree to the collection and use of information in accordance with this policy.</p>
                <p className="text-sm text-gray-600">
                  If you have any questions about this Privacy Policy, please contact us at privacy@sohkinflowers.com
                </p>
              </div>
            </div>

            {/* Information We Collect */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6 flex items-center">
                <Database className="h-8 w-8 text-pink-600 mr-3" />
                Information We Collect
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <UserCheck className="h-5 w-5 text-pink-600 mr-2" />
                    Personal Information
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-pink-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Name and contact details (email, phone number)</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-pink-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Shipping and billing addresses</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-pink-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Payment information (processed securely)</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-pink-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Order history and preferences</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Eye className="h-5 w-5 text-pink-600 mr-2" />
                    Technical Information
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-pink-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>IP address and browser type</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-pink-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Device information</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-pink-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Cookies and usage data</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-pink-600 mt-2 mr-3 flex-shrink-0"></div>
                      <span>Website interaction data</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4">How We Collect Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl font-bold text-blue-600">1</span>
                    </div>
                    <div className="font-bold">Direct Collection</div>
                    <div className="text-sm text-gray-600">When you make a purchase or create an account</div>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl font-bold text-blue-600">2</span>
                    </div>
                    <div className="font-bold">Automated Collection</div>
                    <div className="text-sm text-gray-600">Cookies and website analytics</div>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-3">
                      <span className="text-2xl font-bold text-blue-600">3</span>
                    </div>
                    <div className="font-bold">Third Parties</div>
                    <div className="text-sm text-gray-600">Payment processors and delivery partners</div>
                  </div>
                </div>
              </div>
            </div>

            {/* How We Use Information */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6">How We Use Your Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div className="bg-pink-50 p-6 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mb-4">
                    <ShoppingBag className="h-6 w-6 text-pink-600" />
                  </div>
                  <h3 className="font-bold mb-2">Order Processing</h3>
                  <p className="text-sm text-gray-600">Process and fulfill your orders</p>
                </div>
                <div className="bg-purple-50 p-6 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                    <Truck className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="font-bold mb-2">Delivery</h3>
                  <p className="text-sm text-gray-600">Arrange delivery and provide tracking</p>
                </div>
                <div className="bg-blue-50 p-6 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <Headphones className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold mb-2">Customer Support</h3>
                  <p className="text-sm text-gray-600">Provide customer service and support</p>
                </div>
                <div className="bg-green-50 p-6 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                    <Bell className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="font-bold mb-2">Communication</h3>
                  <p className="text-sm text-gray-600">Send order updates and promotions</p>
                </div>
                <div className="bg-yellow-50 p-6 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
                    <BarChart className="h-6 w-6 text-yellow-600" />
                  </div>
                  <h3 className="font-bold mb-2">Improvement</h3>
                  <p className="text-sm text-gray-600">Improve our website and services</p>
                </div>
                <div className="bg-red-50 p-6 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                    <Shield className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="font-bold mb-2">Security</h3>
                  <p className="text-sm text-gray-600">Protect against fraud and abuse</p>
                </div>
              </div>
            </div>

            {/* Data Protection */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6 flex items-center">
                <Lock className="h-8 w-8 text-pink-600 mr-3" />
                Data Protection & Security
              </h2>
              
              <div className="mb-8">
                <h3 className="text-2xl font-bold mb-4">Security Measures</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h4 className="font-bold mb-3">Technical Security</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>SSL encryption for all data transfers</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Secure payment gateways</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Regular security audits</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 p-6 rounded-xl">
                    <h4 className="font-bold mb-3">Organizational Security</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Limited employee access to data</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Regular staff training</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span>Strict confidentiality agreements</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h3 className="text-xl font-bold mb-4">Data Retention</h3>
                <p className="text-gray-700 mb-4">
                  We retain your personal information only for as long as necessary to fulfill the purposes 
                  outlined in this Privacy Policy, unless a longer retention period is required by law.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="font-bold">Order Data</div>
                    <div className="text-sm text-gray-600">7 years for tax purposes</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="font-bold">Account Information</div>
                    <div className="text-sm text-gray-600">Until account deletion</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <div className="font-bold">Marketing Data</div>
                    <div className="text-sm text-gray-600">Until opt-out</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Rights */}
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold mb-6">Your Rights & Choices</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="font-bold mb-3">Access & Correction</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    You have the right to access and update your personal information.
                  </p>
                  <Link to="/account" className="text-pink-600 font-bold hover:text-pink-700">
                    Update Account →
                  </Link>
                </div>
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="font-bold mb-3">Opt-out of Marketing</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    You can unsubscribe from marketing emails at any time.
                  </p>
                  <a href="#" className="text-pink-600 font-bold hover:text-pink-700">
                    Unsubscribe →
                  </a>
                </div>
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="font-bold mb-3">Data Deletion</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Request deletion of your personal data, subject to legal requirements.
                  </p>
                  <a href="mailto:privacy@sohkinflowers.com" className="text-pink-600 font-bold hover:text-pink-700">
                    Request Deletion →
                  </a>
                </div>
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="font-bold mb-3">Cookie Preferences</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Manage your cookie preferences through browser settings.
                  </p>
                  <a href="#" className="text-pink-600 font-bold hover:text-pink-700">
                    Manage Cookies →
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl p-8 text-white text-center">
              <h2 className="text-3xl font-bold mb-6">Contact Us</h2>
              <p className="text-xl mb-8 text-pink-100 max-w-2xl mx-auto">
                For any privacy-related questions or concerns, please contact our Data Protection Officer
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
                <div className="bg-white/10 p-6 rounded-xl">
                  <Mail className="h-8 w-8 mx-auto mb-4" />
                  <div className="font-bold mb-2">Email</div>
                  <div className="text-pink-200">privacy@sohkinflowers.com</div>
                  <div className="text-sm mt-2">Response within 48 hours</div>
                </div>
                <div className="bg-white/10 p-6 rounded-xl">
                  <Phone className="h-8 w-8 mx-auto mb-4" />
                  <div className="font-bold mb-2">Phone</div>
                  <div className="text-pink-200">1800-123-4567</div>
                  <div className="text-sm mt-2">Mon-Fri, 9 AM - 6 PM</div>
                </div>
              </div>
              
              <div className="mt-8 pt-8 border-t border-white/20">
                <p className="text-pink-200">
                  Sohkin Flowers Pvt. Ltd.<br />
                  123 Flower Street, Mumbai - 400001<br />
                  Maharashtra, India
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;